import { Checkpoint, Mission, MissionStatus } from '../../shared/models/mission.model';

export type MissionSource = Omit<Mission, 'checkpoints'> & {
  checkpoints?: Checkpoint[] | null;
};

export function isMissionStatus(value: unknown): value is MissionStatus {
  return Object.values(MissionStatus).includes(value as MissionStatus);
}

export function normalizeMissionPayload(value: unknown): Mission | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const mission = value as Partial<Mission> & { checkpoints?: Checkpoint[] | null };

  if (typeof mission.id !== 'string' || !isMissionStatus(mission.current_status)) {
    return undefined;
  }

  return normalizeMission(mission as MissionSource);
}

export function normalizeMission(mission: MissionSource): Mission {
  const normalizedMission: Mission = {
    ...mission,
    checkpoints: Array.isArray(mission.checkpoints)
      ? mission.checkpoints.map(normalizeCheckpoint)
      : [],
  };
  const objectNumber = getObjectNumber(mission.object_number);
  const objectDescription = getObjectName(mission.object_description, mission.object_name);
  const objectName = getObjectName(mission.object_name, mission.object_description);

  if (objectNumber !== undefined) {
    normalizedMission.object_number = objectNumber;
  } else {
    delete normalizedMission.object_number;
  }

  if (objectDescription) {
    normalizedMission.object_description = objectDescription;
  } else {
    delete normalizedMission.object_description;
  }

  if (objectName) {
    normalizedMission.object_name = objectName;
  } else {
    delete normalizedMission.object_name;
  }

  return normalizedMission;
}

export function normalizeCheckpoint(checkpoint: Checkpoint): Checkpoint {
  const normalizedCheckpoint: Checkpoint = { ...checkpoint };
  const objectNumber = getObjectNumber(checkpoint.object_number, checkpoint.object_id);
  const objectDescription = getObjectName(checkpoint.object_description, checkpoint.object_name);
  const objectName = getObjectName(checkpoint.object_name, checkpoint.object_description);

  if (objectNumber !== undefined) {
    normalizedCheckpoint.object_number = objectNumber;
    normalizedCheckpoint.object_id = getObjectNumber(checkpoint.object_id) ?? objectNumber;
  } else {
    delete normalizedCheckpoint.object_number;
    delete normalizedCheckpoint.object_id;
  }

  if (objectDescription) {
    normalizedCheckpoint.object_description = objectDescription;
  } else {
    delete normalizedCheckpoint.object_description;
  }

  if (objectName) {
    normalizedCheckpoint.object_name = objectName;
  } else {
    delete normalizedCheckpoint.object_name;
  }

  return normalizedCheckpoint;
}

function getObjectNumber(...values: unknown[]): number | undefined {
  for (const value of values) {
    const number = Number(value);

    if (Number.isFinite(number) && number > 0) {
      return number;
    }
  }

  return undefined;
}

function getObjectName(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value !== 'string') {
      continue;
    }

    const name = value.trim();

    if (name) {
      return name;
    }
  }

  return undefined;
}
